import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Pencil, Trash2 } from 'lucide-react'
import { apiAssetUrl } from '../../config'
import { adminApi } from '../api'
import { Btn, Card, DetailSkeleton } from '../ui'
import { formatDate, StatusBadge, type CaseStudyDetail } from './caseStudiesShared'

type FaqItem = { q: string; a: string }

function parseFaq(raw: unknown): FaqItem[] {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item) => ({
        q: String(item?.q || item?.question || ''),
        a: String(item?.a || item?.answer || ''),
      }))
      .filter((item) => item.q && item.a)
  } catch {
    return []
  }
}

export default function CaseStudyViewPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [caseStudy, setCaseStudy] = useState<CaseStudyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      const { res, data } = await adminApi<{ caseStudy?: CaseStudyDetail; message?: string }>(
        `/api/admin/website/case-studies/${id}`
      )
      if (cancelled) return
      if (!res.ok || !data.caseStudy) {
        setError(data.message || 'Case study not found')
        setCaseStudy(null)
      } else {
        setCaseStudy(data.caseStudy)
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const faqs = useMemo(() => parseFaq(caseStudy?.faq_json), [caseStudy?.faq_json])
  const takeaways = useMemo(
    () =>
      String(caseStudy?.key_takeaways || '')
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [caseStudy?.key_takeaways]
  )
  const howTo = useMemo(() => {
    if (!caseStudy?.how_to_json) return [] as { name: string; text: string }[]
    try {
      const parsed = typeof caseStudy.how_to_json === 'string' ? JSON.parse(caseStudy.how_to_json) : caseStudy.how_to_json
      if (!Array.isArray(parsed)) return []
      return parsed
        .map((item: { name?: string; text?: string }, i: number) => ({
          name: String(item?.name || `Step ${i + 1}`),
          text: String(item?.text || ''),
        }))
        .filter((s: { name: string; text: string }) => s.name && s.text)
    } catch {
      return []
    }
  }, [caseStudy?.how_to_json])
  const relatedQueries = useMemo(
    () =>
      String(caseStudy?.related_queries || '')
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [caseStudy?.related_queries]
  )

  async function remove() {
    if (!caseStudy || !confirm('Delete this case study?')) return
    await adminApi(`/api/admin/website/case-studies/${caseStudy.id}`, { method: 'DELETE' })
    navigate('/admin/case-studies', { replace: true })
  }

  if (loading) {
    return <DetailSkeleton />
  }

  if (error || !caseStudy) {
    return (
      <Card className="space-y-4">
        <p className="text-sm text-red-600">{error || 'Not found'}</p>
        <Btn variant="ghost" onClick={() => navigate('/admin/case-studies')}>
          <ArrowLeft className="h-4 w-4" /> Back to list
        </Btn>
      </Card>
    )
  }

  const seoTitle = caseStudy.seo_title || caseStudy.title
  const seoDesc = caseStudy.seo_description || caseStudy.summary || ''

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Btn variant="ghost" onClick={() => navigate('/admin/case-studies')}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Btn>
        <div className="ml-auto flex flex-wrap gap-2">
          <a
            href={`/case-study/${caseStudy.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-2xl border border-primary/15 bg-white px-3.5 py-2 text-sm font-semibold text-primary"
          >
            Open on site <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Link to={`/admin/case-studies/${caseStudy.id}/edit`}>
            <Btn variant="ghost">
              <Pencil className="h-4 w-4" /> Edit
            </Btn>
          </Link>
          <Btn variant="danger" onClick={() => void remove()}>
            <Trash2 className="h-4 w-4" /> Delete
          </Btn>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-4 sm:gap-5 items-start">
        <Card className="space-y-4 !p-5 sm:!p-7">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge published={!!caseStudy.is_published} />
            {caseStudy.noindex ? (
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[0.7rem] font-bold uppercase text-amber-800">
                Noindex
              </span>
            ) : null}
            <span className="text-xs text-slate-500">{formatDate(caseStudy.published_at)}</span>
            <span className="text-xs text-slate-400">/{caseStudy.slug}</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy-900 leading-tight">
            {caseStudy.title}
          </h2>
          {(caseStudy.client_name || caseStudy.industry) && (
            <p className="text-sm text-slate-500">
              {[caseStudy.client_name, caseStudy.industry].filter(Boolean).join(' · ')}
            </p>
          )}
          {caseStudy.summary && <p className="text-sm sm:text-base text-slate-600">{caseStudy.summary}</p>}
          {(
            [
              ['Conversion', caseStudy.conversion_rate],
              ['Avg ticket', caseStudy.average_ticket_size],
              ['Site visits', caseStudy.total_site_visits],
              ['Duration', caseStudy.campaign_duration],
              ['Pipeline', caseStudy.active_pipeline_value],
            ] as const
          ).some(([, v]) => v) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(
                [
                  ['Conversion', caseStudy.conversion_rate],
                  ['Avg ticket', caseStudy.average_ticket_size],
                  ['Site visits', caseStudy.total_site_visits],
                  ['Duration', caseStudy.campaign_duration],
                  ['Pipeline', caseStudy.active_pipeline_value],
                ] as const
              )
                .filter(([, v]) => v)
                .map(([label, value]) => (
                  <div key={label} className="rounded-2xl border border-primary/10 bg-slate-50/80 px-3 py-2.5">
                    <p className="text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                    <p className="text-sm font-semibold text-navy-900">{value}</p>
                  </div>
                ))}
            </div>
          )}
          {caseStudy.services_delivered && (
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-navy-900">Services: </span>
              {caseStudy.services_delivered}
            </p>
          )}
          {caseStudy.cover_image_path && (
            <img
              src={apiAssetUrl(caseStudy.cover_image_path)}
              alt=""
              className="w-full max-h-80 object-cover rounded-2xl border border-primary/10"
            />
          )}

          {caseStudy.ai_summary && (
            <div className="rounded-2xl border border-primary/15 bg-primary/[0.04] p-4">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-primary mb-2">
                Direct answer (AEO)
              </p>
              <p className="text-sm text-navy-900 leading-relaxed">{caseStudy.ai_summary}</p>
            </div>
          )}

          {takeaways.length > 0 && (
            <div className="rounded-2xl border border-accent/30 bg-accent/10 p-4">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-navy-900 mb-2">
                Key takeaways
              </p>
              <ul className="space-y-1.5 text-sm text-navy-900">
                {takeaways.map((t) => (
                  <li key={t} className="flex gap-2">
                    <span className="text-primary font-bold">•</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {howTo.length > 0 && (
            <div className="rounded-2xl border border-primary/10 p-4 space-y-3">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-navy-900">How-to steps</p>
              <ol className="space-y-2 text-sm text-navy-900 list-decimal pl-4">
                {howTo.map((s) => (
                  <li key={s.name}>
                    <span className="font-semibold">{s.name}</span>
                    <p className="text-slate-600 mt-0.5">{s.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div
            className="prose prose-sm sm:prose max-w-none prose-headings:font-display prose-a:text-primary prose-img:rounded-xl prose-table:w-full border-t border-primary/10 pt-5"
            dangerouslySetInnerHTML={{
              __html: caseStudy.content_html || '<p class="text-slate-400">No content</p>',
            }}
          />

          {faqs.length > 0 && (
            <div className="border-t border-primary/10 pt-5 space-y-3">
              <p className="font-display text-lg font-bold text-navy-900">FAQ</p>
              {faqs.map((f, i) => (
                <div key={i} className="rounded-2xl border border-primary/10 bg-slate-50/70 p-4">
                  <p className="font-semibold text-navy-900">{f.q}</p>
                  <p className="mt-1.5 text-sm text-slate-600 whitespace-pre-wrap">{f.a}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <aside className="space-y-4 lg:sticky lg:top-24">
          <Card className="!p-4 space-y-3">
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-slate-500">
              SEO · AEO · GEO
            </p>
            <MetaRow label="Focus keyword" value={caseStudy.focus_keyword} />
            <MetaRow label="Secondary keywords" value={caseStudy.secondary_keywords} />
            <MetaRow label="SEO title" value={seoTitle} />
            <MetaRow label="Meta description" value={seoDesc} />
            <MetaRow label="AI summary" value={caseStudy.ai_summary} />
            <MetaRow label="Intent" value={caseStudy.search_intent} />
            <MetaRow label="Schema" value={caseStudy.schema_type} />
            <MetaRow
              label="Author"
              value={[caseStudy.author_name, caseStudy.author_role].filter(Boolean).join(' · ')}
            />
            <MetaRow label="Primary city" value={caseStudy.primary_city} />
            <MetaRow label="Locations" value={caseStudy.target_locations} />
            <MetaRow label="Service area" value={caseStudy.service_area} />
            <MetaRow
              label="Canonical"
              value={caseStudy.canonical_path || `/case-study/${caseStudy.slug}`}
            />
            <MetaRow
              label="Robots"
              value={[
                caseStudy.noindex ? 'noindex' : 'index',
                caseStudy.nofollow ? 'nofollow' : 'follow',
                caseStudy.exclude_from_sitemap ? 'no-sitemap' : null,
              ]
                .filter(Boolean)
                .join(', ')}
            />
            {relatedQueries.length > 0 && (
              <div>
                <p className="text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">
                  Related queries
                </p>
                <ul className="mt-1 space-y-0.5 text-sm text-navy-900">
                  {relatedQueries.map((q) => (
                    <li key={q}>· {q}</li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          <Card className="!p-4 space-y-2">
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-slate-500">SERP preview</p>
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1">
              <p className="text-[#1a0dab] text-sm leading-snug font-medium line-clamp-2">{seoTitle}</p>
              <p className="text-[#006621] text-[0.7rem] truncate">
                digisutra.in{caseStudy.canonical_path || `/case-study/${caseStudy.slug}`}
              </p>
              <p className="text-[#4d5156] text-xs leading-snug line-clamp-3">
                {seoDesc || 'No meta description'}
              </p>
            </div>
          </Card>
        </aside>
      </div>
    </div>
  )
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm text-navy-900 break-words">{value?.trim() || '—'}</p>
    </div>
  )
}
